'use client';

import React, { useMemo } from 'react';
import Link from 'next/link';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { IconExternalLink } from '@tabler/icons-react';
import type { SourceRunStatus } from '@/lib/sources/source-types';
import { ROUTES } from '@/lib/routes';
import { getSourceHealth } from '@/lib/dashboard/dashboard-utils';

const COLORS: Record<string, string> = {
  OK: 'var(--chart-1)',
  Warning: 'var(--chart-2)',
  Failed: 'var(--destructive)',
};

function HealthTooltip({ active, payload }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border bg-card px-3 py-2 text-sm shadow-md">
      {payload.map((entry: any) => (
        <p key={entry.name} className="text-muted-foreground">
          {entry.name}: <span className="font-semibold text-foreground">{entry.value}</span>
        </p>
      ))}
    </div>
  );
}

interface SourceHealthCardProps {
  sourceStatuses: SourceRunStatus[];
}

export const SourceHealthCard = React.memo(function SourceHealthCard({
  sourceStatuses,
}: SourceHealthCardProps) {
  const health = useMemo(
    () => getSourceHealth(sourceStatuses),
    [sourceStatuses]
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Source Health</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Donut chart */}
        {health.pieData.length > 0 && (
          <div className="h-40 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={health.pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={65}
                  paddingAngle={3}
                  dataKey="value"
                  nameKey="name"
                >
                  {health.pieData.map((entry) => (
                    <Cell
                      key={entry.name}
                      fill={COLORS[entry.name] || 'hsl(var(--muted))'}
                    />
                  ))}
                </Pie>
                <Tooltip content={<HealthTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Legend / counts */}
        <div className="flex items-center justify-center gap-4 text-sm">
          <span className="flex items-center gap-1.5">
            <span className="size-2.5 rounded-full bg-emerald-500" />
            OK: {health.ok}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="size-2.5 rounded-full bg-yellow-500" />
            Warning: {health.warning}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="size-2.5 rounded-full bg-red-500" />
            Failed: {health.error}
          </span>
        </div>

        {/* Failed sources */}
        {health.failedList.length > 0 && (
          <div className="space-y-1.5">
            {health.failedList.map((s) => (
              <div
                key={s.sourceId}
                className="flex items-center gap-2 rounded-md border border-destructive/20 bg-destructive/5 px-2.5 py-1.5"
              >
                <Badge variant="destructive" className="text-[10px] px-1.5 py-0">
                  Failed
                </Badge>
                <span className="text-xs font-medium truncate">{s.name}</span>
                {s.message && (
                  <span className="text-xs text-muted-foreground line-clamp-1 ml-auto">
                    {s.message}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Warning sources */}
        {health.warningList.length > 0 && (
          <div className="space-y-1.5">
            {health.warningList.map((s) => (
              <div
                key={s.sourceId}
                className="flex items-center gap-2 rounded-md border border-yellow-500/20 bg-yellow-500/5 px-2.5 py-1.5"
              >
                <Badge
                  variant="outline"
                  className="text-[10px] px-1.5 py-0 border-yellow-500/50 text-yellow-600"
                >
                  Warning
                </Badge>
                <span className="text-xs font-medium truncate">{s.name}</span>
                {s.message && (
                  <span className="text-xs text-muted-foreground line-clamp-1 ml-auto">
                    {s.message}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Alert text */}
        {health.failedList.length > 0 && (
          <p className="text-xs text-destructive">
            Some sources failed. Project results may be incomplete.
          </p>
        )}

        {/* CTA */}
        <Button variant="outline" size="sm" className="w-full" asChild>
          <Link href={ROUTES.sources}>
            <IconExternalLink className="size-3.5 mr-1.5" />
            View sources
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
});
